/**
 * @fileoverview Messages page component for SMS conversation management.
 * Displays a split-view inbox with conversation list and thread view,
 * with responsive mobile support, URL-based navigation, and Realtime updates.
 */

import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { MessageSquare, RefreshCw } from "lucide-react";
import { MainLayout } from "@/components/layout/MainLayout";
import { ConversationList } from "@/components/messages/ConversationList";
import { ConversationThread } from "@/components/messages/ConversationThread";
import { MessageEmptyState } from "@/components/messages/MessageEmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useConversations } from "@/hooks/useMessages";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

/**
 * Main messages inbox page component.
 * Handles conversation selection, URL sync, Realtime updates, and responsive layout.
 */
export default function MessagesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPhone, setSelectedPhone] = useState<string | null>(
    searchParams.get("phone") || null
  );
  const { data: threads = [], isLoading } = useConversations();
  const isMobile = useIsMobile();
  const { activeTenantId } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isEnsuringWebhooks, setIsEnsuringWebhooks] = useState(false);

  const ensureWebhooksConfigured = useCallback(
    async ({
      force = false,
      toastOnSuccess = true,
    }: { force?: boolean; toastOnSuccess?: boolean } = {}) => {
      if (!activeTenantId) return;

      const storageKey = `calie_webhooks_configured_${activeTenantId}`;
      const alreadyConfigured =
        typeof window !== "undefined" &&
        window.sessionStorage.getItem(storageKey) === "1";

      if (alreadyConfigured && !force) return;

      // Get current session to pass auth header explicitly
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session?.access_token) {
        console.warn('[MessagesPage] No session available for webhook configuration');
        return;
      }

      setIsEnsuringWebhooks(true);
      try {
        const { data, error } = await supabase.functions.invoke(
          "Calie_configureVoiceWebhook",
          {
            headers: {
              Authorization: `Bearer ${sessionData.session.access_token}`,
            },
          }
        );
        if (error) throw error;

        if (typeof window !== "undefined") {
          window.sessionStorage.setItem(storageKey, "1");
        }

        const configured = data?.configured ?? 0;
        const total = data?.total ?? 0;
        const errors = data?.errors as string[] | undefined;

        if (toastOnSuccess) {
          if (errors?.length) {
            toast({
              title: "Webhooks configured with warnings",
              description: `${configured}/${total} numbers updated. Some inbound SMS may still not route.`,
              variant: "destructive",
            });
          } else {
            toast({
              title: "SMS webhooks configured",
              description: `${configured}/${total} numbers updated. Reply again to test inbound SMS.`,
            });
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Unknown error";
        toast({
          title: "Failed to configure SMS webhooks",
          description: msg,
          variant: "destructive",
        });
      } finally {
        setIsEnsuringWebhooks(false);
      }
    },
    [activeTenantId, toast]
  );

  const handleRefresh = useCallback(async () => {
    if (!activeTenantId) return;

    setIsRefreshing(true);
    try {
      // Re-apply Twilio webhook configuration (common cause of missing inbound SMS)
      await ensureWebhooksConfigured({ force: true, toastOnSuccess: false });

      await Promise.all([
        queryClient.refetchQueries({
          queryKey: ["conversations", activeTenantId],
        }),
        selectedPhone
          ? queryClient.refetchQueries({
              queryKey: ["conversation-thread", activeTenantId, selectedPhone],
            })
          : Promise.resolve(),
        selectedPhone
          ? queryClient.refetchQueries({
              queryKey: ["opt-out-status", activeTenantId, selectedPhone],
            })
          : Promise.resolve(),
      ]);

      toast({ title: "Refreshed", description: "Latest messages loaded." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast({
        title: "Refresh failed",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [
    activeTenantId,
    ensureWebhooksConfigured,
    queryClient,
    selectedPhone,
    toast,
  ]);

  useEffect(() => {
    // Ensure webhooks once per session so inbound SMS reaches Calie_inboundSMS
    ensureWebhooksConfigured({ force: false, toastOnSuccess: true });
  }, [ensureWebhooksConfigured]);

  // Sync URL with selected phone
  useEffect(() => {
    if (selectedPhone) {
      setSearchParams({ phone: selectedPhone });
    } else {
      setSearchParams({});
    }
  }, [selectedPhone, setSearchParams]);

  // Subscribe to Realtime updates for new SMS messages
  useEffect(() => {
    if (!activeTenantId) return;

    console.debug(
      "[MessagesPage] Setting up Realtime subscription for tenant:",
      activeTenantId
    );

    const channel = supabase
      .channel(`sms-updates-${activeTenantId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "calie_sms_conversations",
          filter: `tenant_id=eq.${activeTenantId}`,
        },
        (payload) => {
          console.debug(
            "[MessagesPage] New SMS received via Realtime:",
            payload.new
          );

          const newMessage = payload.new as {
            message_direction: string;
            phone_number: string;
            message_body: string;
          };

          // Invalidate the conversations list to refresh sidebar
          queryClient.invalidateQueries({
            queryKey: ["conversations", activeTenantId],
          });

          // Also invalidate the specific thread if it matches the selected phone
          if (newMessage.phone_number === selectedPhone) {
            queryClient.invalidateQueries({
              queryKey: ["conversation-thread", activeTenantId, selectedPhone],
            });
          }

          // If it's an inbound message, show a toast notification
          if (newMessage.message_direction === "inbound") {
            toast({
              title: `New message from ${newMessage.phone_number}`,
              description:
                newMessage.message_body?.slice(0, 50) +
                (newMessage.message_body?.length > 50 ? "..." : ""),
            });

            // Request browser notification permission if not granted
            if (
              "Notification" in window &&
              Notification.permission === "default"
            ) {
              Notification.requestPermission();
            }

            // Show browser notification if permitted
            if (
              "Notification" in window &&
              Notification.permission === "granted"
            ) {
              new Notification("New SMS", {
                body: `From ${
                  newMessage.phone_number
                }: ${newMessage.message_body?.slice(0, 100)}`,
                icon: "/favicon.ico",
              });
            }
          }
        }
      )
      .subscribe((status) => {
        console.debug("[MessagesPage] Realtime subscription status:", status);
      });

    return () => {
      console.debug("[MessagesPage] Cleaning up Realtime subscription");
      supabase.removeChannel(channel);
    };
  }, [activeTenantId, selectedPhone, queryClient, toast]);

  const selectedThread =
    threads.find((t) => t.phone_number === selectedPhone) || null;
  const hasConversations = threads.length > 0;

  // Mobile: show either list or thread
  const showList = isMobile ? !selectedPhone : true;
  const showThread = isMobile ? !!selectedPhone : true;

  return (
    <MainLayout noPadding>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex-shrink-0 bg-background border-b border-border p-4 flex items-center gap-3">
          {isMobile && selectedPhone && (
            <button
              onClick={() => setSelectedPhone(null)}
              className="text-sm text-primary hover:underline"
            >
              ← Back
            </button>
          )}
          <MessageSquare className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-semibold text-foreground">Messages</h1>
          {!isLoading && (
            <span className="text-sm text-muted-foreground">
              {threads.length}{" "}
              {threads.length === 1 ? "conversation" : "conversations"}
            </span>
          )}
          <div className="ml-auto">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              disabled={isRefreshing || isEnsuringWebhooks}
              title={isRefreshing ? "Refreshing…" : "Refresh messages"}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {!hasConversations && !isLoading ? (
            <MessageEmptyState />
          ) : (
            <>
              {showList && (
                <ErrorBoundary>
                  <div
                    className={
                      isMobile ? "w-full" : "w-80 lg:w-96 flex-shrink-0"
                    }
                  >
                    <ConversationList
                      threads={threads}
                      selectedPhone={selectedPhone}
                      onSelectThread={setSelectedPhone}
                      isLoading={isLoading}
                    />
                  </div>
                </ErrorBoundary>
              )}
              {showThread && (
                <ErrorBoundary>
                  <ConversationThread
                    thread={selectedThread}
                    phoneNumber={selectedPhone}
                  />
                </ErrorBoundary>
              )}
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
